'use client';
/** @jsxImportSource @emotion/react */

import {
  type KeyboardEvent,
  type ClipboardEvent,
  type SyntheticEvent,
  useState,
} from 'react';

import {
  type AutocompleteInputChangeReason,
  type AutocompleteRenderGetTagProps,
} from '@mui/material/Autocomplete';

import {
  Autocomplete,
  type AutocompleteProps,
} from '../autocomplete/Autocomplete';
import { Chip, type ChipProps } from '../chip/Chip';
import { TextField, type TextFieldProps } from '../text-field/TextField';
import { includesValue, sanitizeInput, splitPastedValues } from './utils';

type MultiInputFieldProps = Omit<
  AutocompleteProps<string, true, false, true>,
  'multiple' | 'onChange' | 'renderInput' | 'value'
> & {
  /**
   * The array of values of the MultiInputField
   */
  values: string[];
  /**
   * Callback fired when the value changes
   */
  onChange: (values: string[]) => void;
  /**
   * Props to apply to the `TextField`
   */
  getTextFieldProps?: (values: string[]) => Partial<TextFieldProps>;
  /**
   * Props to apply to the `Chip`
   */
  getChipProps?: (value: string) => Partial<ChipProps>;
  /**
   * Disables case sensitivity when comparing values for duplicates
   */
  disableCaseSensitivity?: boolean;
};

const MultiInputField = ({
  values = [],
  options = [],
  autoHighlight = false,
  onChange,
  getTextFieldProps,
  getChipProps,
  disableCaseSensitivity = false,
  ...rest
}: MultiInputFieldProps) => {
  const [inputValue, setInputValue] = useState<string>('');

  const addValue = (newValue: string) => {
    const sanitized = sanitizeInput(newValue);
    if (
      sanitized &&
      !includesValue(values, sanitized, disableCaseSensitivity)
    ) {
      onChange?.([...values, sanitized]);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Unlike ',' and 'Tab', Autocomplete handles 'Enter' itself (selecting a
    // highlighted option, toggling one off, or committing free text) via its
    // own `onChange` `reason`. Calling `addValue` here too would race that
    // and double-process the same keystroke, so 'Enter' is deliberately left
    // out of `keyTriggers` — see the `reason`-based branch in `onChange` below.
    const keyTriggers = [',', 'Tab'];

    if (keyTriggers.includes(event.key) && inputValue.trim() !== '') {
      event.preventDefault();
      addValue(inputValue);
      setInputValue('');
      return;
    }

    // Autocomplete only clears the input when its own value array changes
    // length; retyping a value that's already selected leaves the array
    // content-identical, so it never resets the input. Clear it ourselves,
    // independent of whatever Autocomplete decided to do with the value.
    if (event.key === 'Enter' && inputValue.trim() !== '') {
      setInputValue('');
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text');
    const parsed = splitPastedValues(pasted, disableCaseSensitivity);

    if (parsed.length === 0) {
      return;
    }

    event.preventDefault();

    const newItems = parsed.filter(
      (item) => !includesValue(values, item, disableCaseSensitivity)
    );
    if (newItems.length > 0) {
      onChange?.([...values, ...newItems]);
    }

    setInputValue('');
  };

  const handleInputChange = (
    event: SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    const isComposing = (event?.nativeEvent as InputEvent)?.isComposing;
    const isBatchInsert = newInputValue.length - inputValue.length > 1;
    const endsWithDelimiter = /[,;]\s*$/.test(newInputValue);

    if (
      reason === 'input' &&
      !isComposing &&
      /[,;]/.test(newInputValue) &&
      (isBatchInsert || endsWithDelimiter)
    ) {
      const parsed = splitPastedValues(newInputValue, disableCaseSensitivity);
      const newItems = parsed.filter(
        (item) => !includesValue(values, item, disableCaseSensitivity)
      );

      if (newItems.length > 0) {
        onChange?.([...values, ...newItems]);
        setInputValue('');
        return;
      }

      if (parsed.length > 0) {
        setInputValue('');
        return;
      }
    }

    setInputValue(newInputValue);
  };

  return (
    <Autocomplete
      {...rest}
      autoHighlight={autoHighlight}
      multiple
      freeSolo
      inputValue={inputValue}
      onInputChange={handleInputChange}
      value={values}
      options={options ?? []}
      onChange={(_, autocompleteValues, reason) => {
        if (
          reason === 'selectOption' ||
          reason === 'createOption' ||
          reason === 'blur'
        ) {
          const newItems = (autocompleteValues as string[]).filter(
            (v) => !includesValue(values, v, disableCaseSensitivity)
          );
          newItems.forEach(addValue);
          return;
        }

        onChange?.(autocompleteValues as string[]);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          {...(getTextFieldProps?.(values) ?? {})}
        />
      )}
      renderTags={(tagValue, getTagProps: AutocompleteRenderGetTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const extraChipProps = getChipProps?.(option) ?? {};

          return (
            <Chip key={key} label={option} {...extraChipProps} {...tagProps} />
          );
        })
      }
    />
  );
};

export { MultiInputField, type MultiInputFieldProps };
